import debounce from "debounce";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { CrossIcon, SearchIcon } from "src/assets";
import TextInput from "src/components/Inputs/TextInput";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { EvsesCompatibility, SESSION_KEYS } from "src/consts/session";
import { Box, List, Stack, Divider, ListItem, IconButton } from "@mui/material";
import EvseListItem from "src/components/SmartCharging/Components/EvseListItem";

const ManufacturerSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  const [getEvsesCompatibility] = useSessionStorage<EvsesCompatibility[]>(
    SESSION_KEYS.EVSES_COMPATIBILITY,
  );

  const { watch, reset, control, getValues } = useForm<{
    searchText: string;
    manufacturersList: string[];
    manufacturersListUnfiltered: string[];
  }>({
    defaultValues: {
      searchText: "",
      manufacturersList: [],
      manufacturersListUnfiltered: [],
    },
  });

  useEffect(() => {
    const evsesCompatibility = getEvsesCompatibility();
    if (evsesCompatibility) {
      const filteredManufacturers = evsesCompatibility.filter(
        (evse) =>
          evse.compatibilityStatus === "BASIC" ||
          evse.compatibilityStatus === "ADVANCED",
      );
      const manufacturersList = filteredManufacturers.map(
        (evse) => evse.manufacturer,
      );
      const manufacturersListDistinct = manufacturersList.filter(
        (manufacturer, index, list) => list.indexOf(manufacturer) === index,
      );

      reset({
        searchText: "",
        manufacturersList: manufacturersListDistinct,
        manufacturersListUnfiltered: manufacturersListDistinct,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debouncedCb = debounce(async (value, { name }) => {
      if (name === "searchText") {
        const filteredManufacturersList =
          getValues().manufacturersListUnfiltered.filter((manufacturer) => {
            return manufacturer
              .toLowerCase()
              .startsWith(value.searchText.toLowerCase());
          });
        reset({
          searchText: value.searchText,
          manufacturersList: filteredManufacturersList,
          manufacturersListUnfiltered: getValues().manufacturersListUnfiltered,
        });
      }
    }, 500);
    const subscription = watch(debouncedCb);
    return () => subscription.unsubscribe();
  }, [getValues, reset, watch]);

  const handleManufacturerClick = (manufacturer: string) => {
    return `${navigate(
      SMART_CHARGING_ROUTES.MODEL_SEARCH + `?manufacturer=${manufacturer}`,
    )}`;
  };

  const clearAll = () => {
    reset({
      searchText: "",
      manufacturersList: getValues().manufacturersListUnfiltered,
      manufacturersListUnfiltered: getValues().manufacturersListUnfiltered,
    });
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.wallboxCompatibilityManufacturerSearch.formTitle")}
        sx={(theme) => ({
          fontSize: "24px",
          fontWeight: "500",
          lineHeight: "28px",
          fontFamily: "officina",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Stack>
        <TextInput
          fullWidth
          labelText={""}
          placeholder="Hersteller Suche"
          control={control}
          name="searchText"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: <SearchIcon />,
            endAdornment: (
              <IconButton aria-label="clear" onClick={clearAll}>
                <CrossIcon />
              </IconButton>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                border: "1px solid #D5D5D5",
              },
              "&:hover fieldset": {
                border: "1px solid #D5D5D5",
              },
              "&.Mui-focused fieldset": {
                border: "1px solid #D5D5D5",
              },
            },

            ".MuiFormLabel-root": {
              color: "#767676",
            },
          }}
        />
      </Stack>

      <Box
        sx={(theme) => ({
          px: "16px",
          borderRadius: "12px",
          bgcolor: theme.palette.white.main,
        })}
      >
        <List>
          {getValues().manufacturersList &&
            getValues().manufacturersList.map((manufacturer, index) => (
              <>
                <EvseListItem
                  itemText={manufacturer}
                  handleItemClick={() => handleManufacturerClick(manufacturer)}
                />
                {getValues().manufacturersList.length !== index + 1 && (
                  <Divider sx={{ ml: "16px", mr: "16px" }} />
                )}
              </>
            ))}
          <ListItem disablePadding></ListItem>
        </List>
      </Box>
    </Stack>
  );
};

export default ManufacturerSearch;
